import { useState } from "react";
import { AlertTriangle, X, Eye, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { useSpamAlerts, SpamAlert } from "@/hooks/useSpamAlerts";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface SpamAlertBannerProps {
  establishmentId: string | null;
}

const alertTypeLabels: Record<SpamAlert['alertType'], string> = {
  high_volume: "Volume élevé de réservations",
  same_ip: "Multiples réservations depuis la même IP",
  same_email: "Multiples réservations avec le même email",
  suspicious_pattern: "Pattern suspect détecté",
};

const alertTypeIcons: Record<SpamAlert['alertType'], string> = {
  high_volume: "📊",
  same_ip: "🌐",
  same_email: "📧",
  suspicious_pattern: "🔍",
};

export const SpamAlertBanner = ({ establishmentId }: SpamAlertBannerProps) => {
  const { user } = useAuth();
  const { unacknowledgedAlerts, acknowledgeAlert, acknowledgeAllAlerts } = useSpamAlerts({
    establishmentId: establishmentId || undefined,
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const [dismissing, setDismissing] = useState<string | null>(null);

  if (unacknowledgedAlerts.length === 0) return null;

  const handleAcknowledge = async (alertId: string) => {
    if (!user?.id) return;
    setDismissing(alertId);
    const success = await acknowledgeAlert(alertId, user.id);
    if (success) {
      toast({
        title: "Alerte acquittée",
        description: "L'alerte a été marquée comme lue.",
      });
    }
    setDismissing(null);
  };

  const handleAcknowledgeAll = async () => {
    if (!user?.id) return;
    const success = await acknowledgeAllAlerts(user.id);
    if (success) {
      toast({
        title: "Toutes les alertes acquittées",
        description: `${unacknowledgedAlerts.length} alerte(s) marquée(s) comme lue(s).`,
      });
    }
  };

  const latestAlert = unacknowledgedAlerts[0];

  return (
    <Card className="mb-4 border-yellow-500 bg-yellow-50 dark:bg-yellow-950/30">
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <div className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-semibold text-yellow-800 dark:text-yellow-200">
                    Alerte de sécurité
                  </h4>
                  <Badge variant="outline" className="border-yellow-500 text-yellow-700 dark:text-yellow-300">
                    {unacknowledgedAlerts.length} non lue{unacknowledgedAlerts.length > 1 ? 's' : ''}
                  </Badge>
                </div>
                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                  {alertTypeIcons[latestAlert.alertType]} {alertTypeLabels[latestAlert.alertType]}
                  {latestAlert.details.count && latestAlert.details.periodMinutes && (
                    <span className="ml-1">
                      : {latestAlert.details.count} réservations en {latestAlert.details.periodMinutes} min
                      {latestAlert.details.threshold && ` (seuil: ${latestAlert.details.threshold})`}
                    </span>
                  )}
                </p>
                <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                  {formatDistanceToNow(latestAlert.createdAt, { addSuffix: true, locale: fr })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {unacknowledgedAlerts.length > 1 && (
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-yellow-700">
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                    <span className="ml-1 hidden sm:inline">
                      {isExpanded ? "Réduire" : "Voir tout"}
                    </span>
                  </Button>
                </CollapsibleTrigger>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAcknowledge(latestAlert.id)}
                disabled={dismissing === latestAlert.id}
                className="border-yellow-500 text-yellow-700 hover:bg-yellow-100"
              >
                <Eye className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Acquitter</span>
              </Button>
              {unacknowledgedAlerts.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleAcknowledgeAll}
                  className="text-yellow-700"
                >
                  <X className="h-4 w-4" />
                  <span className="hidden sm:inline ml-1">Tout acquitter</span>
                </Button>
              )}
            </div>
          </div>

          <CollapsibleContent>
            {unacknowledgedAlerts.length > 1 && (
              <div className="mt-4 space-y-2 border-t border-yellow-300 pt-4">
                {unacknowledgedAlerts.slice(1).map((alert) => (
                  <div
                    key={alert.id}
                    className="flex items-center justify-between p-2 bg-yellow-100/50 dark:bg-yellow-900/20 rounded"
                  >
                    <div className="flex-1">
                      <p className="text-sm text-yellow-800 dark:text-yellow-200">
                        {alertTypeIcons[alert.alertType]} {alertTypeLabels[alert.alertType]}
                        {alert.details.count && alert.details.periodMinutes && (
                          <span className="ml-1">
                            : {alert.details.count} en {alert.details.periodMinutes} min
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-yellow-600 dark:text-yellow-400">
                        {formatDistanceToNow(alert.createdAt, { addSuffix: true, locale: fr })}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleAcknowledge(alert.id)}
                      disabled={dismissing === alert.id}
                      className="text-yellow-700"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CollapsibleContent>
        </div>
      </Collapsible>
    </Card>
  );
};
